import React, { useState, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './context/AuthContext'
import Header from './components/Layout/Header'
import AuthModal from './components/Auth/AuthModal'
import FileUpload from './components/Upload/FileUpload'
import BookMetadataForm from './components/Upload/BookMetadataForm'
import BookLibrary from './components/Library/BookLibrary'
import MockupTemplates from './components/Mockup/MockupTemplates'
import MockupGenerator from './components/Mockup/MockupGenerator'
import { useAuth } from './context/AuthContext'
import { useBookCovers } from './hooks/useBookCovers'
import { initializeSupabase, createStorageBuckets } from './utils/supabaseSetup'
import { motion } from 'framer-motion'
import SafeIcon from './common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'
import './App.css'

const { FiUpload, FiImage, FiSettings, FiDownload } = FiIcons

const AppContent = () => {
  const { user } = useAuth()
  const { uploadCover } = useBookCovers()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [currentStep, setCurrentStep] = useState('upload')
  const [uploadedFile, setUploadedFile] = useState(null)
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null)
  const [bookMetadata, setBookMetadata] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [currentCover, setCurrentCover] = useState(null)
  const [currentCoverData, setCurrentCoverData] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    // Initialize Supabase setup
    const init = async () => {
      await initializeSupabase()
      await createStorageBuckets()
      setInitialized(true)
    }
    init()
  }, [])

  const handleFileUpload = async (file) => {
    if (!user) {
      setShowAuthModal(true)
      return
    }

    console.log('File uploaded:', file.name)
    setUploadedFile(file)
    setUploadedFileUrl(URL.createObjectURL(file))
    setCurrentStep('metadata')
  }

  const handleMetadataSubmit = async (metadata) => {
    if (!uploadedFile || !user) {
      console.error('Missing file or user for metadata submit')
      toast.error('Missing file or user information')
      return
    }

    console.log('Submitting metadata:', metadata)
    setUploading(true)
    
    try {
      const result = await uploadCover(uploadedFile, metadata)
      console.log('Upload result:', result)
      
      if (result) {
        setBookMetadata(metadata)
        setCurrentCover(result.file_url)
        setCurrentCoverData(result)
        setCurrentStep('templates')
        toast.success('Book cover uploaded successfully!')
      } else {
        throw new Error('Failed to upload cover - no result returned')
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast.error(error.message || 'Failed to upload book cover')
    } finally {
      setUploading(false)
    }
  }

  const handleCoverSelect = (coverUrl, metadata) => {
    setCurrentCover(coverUrl)
    setBookMetadata(metadata)
    setCurrentCoverData(metadata)
    setCurrentStep('templates')
  }

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setCurrentStep('generate')
  }

  const resetToUpload = () => {
    setCurrentStep('upload')
    setUploadedFile(null)
    setUploadedFileUrl(null)
    setBookMetadata(null)
    setSelectedTemplate(null)
    setCurrentCover(null)
    setCurrentCoverData(null)
  }

  const steps = [
    { id: 'upload', name: 'Upload', icon: FiUpload },
    { id: 'library', name: 'Library', icon: FiImage },
    { id: 'templates', name: 'Templates', icon: FiSettings },
    { id: 'generate', name: 'Generate', icon: FiDownload }
  ]

  if (!initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing BookCover Studio...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      <Header onAuthClick={() => setShowAuthModal(true)} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user ? (
          <>
            {/* Debug Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-yellow-800 mb-2">Debug Info:</h4>
              <div className="text-sm text-yellow-700 space-y-1">
                <p>Current Step: {currentStep}</p>
                <p>User: {user?.email}</p>
                <p>Uploaded File: {uploadedFile?.name || 'None'}</p>
                <p>Current Cover: {currentCover ? 'Yes' : 'No'}</p>
                <p>Book Metadata: {bookMetadata ? 'Set' : 'None'}</p>
                <p>Uploading: {uploading ? 'Yes' : 'No'}</p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex justify-center">
                <div className="flex items-center space-x-4 bg-white rounded-full px-6 py-3 shadow-lg">
                  {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                      <button
                        onClick={() => {
                          if (step.id === 'upload') resetToUpload()
                          else if (step.id === 'library') setCurrentStep('library')
                          else if (step.id === 'templates' && currentCover) setCurrentStep('templates')
                          else if (step.id === 'generate' && selectedTemplate) setCurrentStep('generate')
                        }}
                        disabled={
                          (step.id === 'templates' && !currentCover) ||
                          (step.id === 'generate' && !selectedTemplate)
                        }
                        className={`
                          flex items-center space-x-2 px-4 py-2 rounded-full transition-all
                          ${currentStep === step.id
                            ? 'bg-primary-600 text-white'
                            : currentStep === 'upload' && step.id !== 'upload'
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                          }
                        `}
                      >
                        <SafeIcon icon={step.icon} className="w-4 h-4" />
                        <span className="text-sm font-medium">{step.name}</span>
                      </button>
                      {index < steps.length - 1 && (
                        <div className="w-8 h-px bg-gray-300" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              {currentStep === 'upload' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto"
                >
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
                      Create Professional Book Mockups
                    </h2>
                    <p className="text-lg text-gray-600">
                      Transform your book cover into stunning marketing materials
                    </p>
                  </div>
                  <FileUpload onUpload={handleFileUpload} loading={uploading} />
                </motion.div>
              )}

              {currentStep === 'metadata' && uploadedFileUrl && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="bg-white rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                      <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={uploadedFileUrl}
                          alt="Uploaded cover"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                  <BookMetadataForm
                    onSubmit={handleMetadataSubmit}
                    loading={uploading}
                  />
                </div>
              )}

              {currentStep === 'library' && (
                <BookLibrary onSelectCover={handleCoverSelect} />
              )}

              {currentStep === 'templates' && currentCover && (
                <div>
                  {bookMetadata && (
                    <div className="bg-white rounded-xl p-6 mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-24 bg-gray-100 rounded overflow-hidden">
                          <img
                            src={currentCover}
                            alt={bookMetadata.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {bookMetadata.title}
                          </h3>
                          <p className="text-gray-600">by {bookMetadata.author}</p>
                          {bookMetadata.genre && (
                            <span className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full mt-1">
                              {bookMetadata.genre}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <MockupTemplates
                    selectedTemplate={selectedTemplate}
                    onSelectTemplate={handleTemplateSelect}
                    coverImage={currentCover}
                  />
                </div>
              )}

              {currentStep === 'generate' && currentCover && selectedTemplate && (
                <MockupGenerator
                  coverImage={currentCover}
                  template={selectedTemplate}
                  bookData={bookMetadata}
                  onDownload={() => {
                    toast.success('Mockup downloaded successfully!')
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="max-w-3xl mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <SafeIcon icon={FiImage} className="w-12 h-12 text-white" />
              </div>
              
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
                BookCover Studio
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Create professional book mockups for paperbacks, hardcovers, and e-readers. 
                Perfect for social media, marketing campaigns, and promotional materials.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiUpload} className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Easy Upload</h3>
                  <p className="text-gray-600 text-sm">Drag and drop your book covers</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiSettings} className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Multiple Formats</h3>
                  <p className="text-gray-600 text-sm">Paperback, hardcover, and e-readers</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiDownload} className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">High Quality</h3>
                  <p className="text-gray-600 text-sm">Download in PNG, JPG, or PDF</p>
                </div>
              </div>

              <button
                onClick={() => setShowAuthModal(true)}
                className="bg-primary-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-700 transition-colors shadow-lg"
              >
                Get Started Free
              </button>
            </div>
          </motion.div>
        )}
      </main>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
      
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App